import { ArrowRight, CheckCircle, Wrench, TruckIcon, Settings } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import homeData from '../../data/home.json';
import productsData from '../../data/products.json';

interface HomeProps {
  onNavigate: (page: string, params?: any) => void;
}

export function Home({ onNavigate }: HomeProps) {
  // 图标映射
  const iconMap = [
    <Settings size={48} />,
    <TruckIcon size={48} />,
    <Wrench size={48} />
  ];
  
  const companyAdvantages = homeData.companyAdvantages.map((item, index) => ({
    ...item,
    icon: iconMap[index]
  }));

  const productCategories = homeData.productCategories;
  // 从 products.json 获取最新的4个产品
  const latestProducts = productsData.slice(0, 4);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <ImageWithFallback
          src="https://images.unsplash.com/photo-1684695747624-0dd1b6412bc1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwYnVpbGRpbmd8ZW58MXx8fHwxNzY3NzE5ODU4fDA&ixlib=rb-4.1.0&q=80&w=1080"
          alt="Industrial facility"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl mb-6">
            Professional Aerial Work Platform Parts Provider
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200">
            High Quality Parts For AWP Machines
          </p>
          <button
            onClick={() => onNavigate('Product')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg text-lg transition-colors inline-flex items-center gap-2"
          >
            View Products
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Company Advantage Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl text-center mb-4">
            Company <span className="text-primary">Advantage</span>
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto mb-12"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {companyAdvantages.map((advantage) => (
              <div
                key={advantage.id}
                className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow relative"
              >
                {/* Primary color corner */}
                <div className="absolute top-0 left-0 w-0 h-0 border-t-[80px] border-t-primary border-r-[80px] border-r-transparent"></div>
                
                <div className="p-8">
                  <div className="flex justify-center mb-6 text-gray-600 relative z-10">
                    {advantage.icon}
                  </div>
                  <h3 className="text-xl text-center mb-4">{advantage.title}</h3>
                  <p className="text-gray-600 text-center text-sm leading-relaxed">
                    {advantage.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCategories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all group"
              >
                <div 
                  className="relative h-48 overflow-hidden cursor-pointer"
                  onClick={() => onNavigate('Product', { category: category.id })}
                >
                  <ImageWithFallback
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div 
                  className="bg-primary py-3 px-4 cursor-pointer"
                  onClick={() => onNavigate('Product', { category: category.id })}
                >
                  <h3 className="text-center text-primary-foreground">{category.name}</h3>
                </div>
                <div className="p-4">
                  <ul className="space-y-2">
                    {category.items.map((item: any, idx: number) => (
                      <li 
                        key={idx} 
                        className="text-gray-600 text-sm flex items-center gap-2 cursor-pointer hover:text-primary transition-colors"
                        onClick={() => onNavigate('Product', { category: category.id, brand: item.brand })}
                      >
                        <span className="text-primary">›</span>
                        {item.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Products Section */}
      <section className="py-16 bg-primary bg-opacity-90 relative overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0 opacity-20">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1684695747624-0dd1b6412bc1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwYnVpbGRpbmd8ZW58MXx8fHwxNzY3NzE5ODU4fDA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl text-center mb-4 text-white">
            New <span className="text-primary-foreground font-bold">Products</span>
          </h2>
          <div className="w-24 h-1 bg-white mx-auto mb-12"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {latestProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => onNavigate('ProductDetail', { productId: product.id })}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer"
              >
                <div className="relative h-48 bg-gray-50 flex items-center justify-center">
                  <ImageWithFallback
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg mb-2 line-clamp-2 min-h-[56px]">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {product.description}
                  </p>
                  <button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-2 rounded transition-colors flex items-center justify-between px-4">
                    <span>READ MORE</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button
              onClick={() => onNavigate('Product')}
              className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-3 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              READ MORE
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl mb-6">About Our Company</h2>
              <p className="text-lg text-gray-700 mb-4">
                We are a leading supplier of engineering parts and components with over 20 years
                of experience in the industry. Our commitment to quality and customer satisfaction
                has made us the trusted choice for businesses worldwide.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                We offer a comprehensive range of products including hydraulic parts, bearings,
                seals, and gears, all manufactured to the highest standards.
              </p>
              <button
                onClick={() => onNavigate('About Us')}
                className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 rounded-lg transition-colors"
              >
                Learn More
              </button>
            </div>
            <div className="relative h-[400px] rounded-lg overflow-hidden shadow-lg">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1717386255886-6ae56e497f9a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmYWN0b3J5JTIwZXF1aXBtZW50fGVufDF8fHx8MTc2NzcxNjE3N3ww&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Factory equipment"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}